import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIssueNumber } from "@/hooks/useIssueNumber";
import { NOTIFY_ON_READY } from "@/lib/featureFlags";
import PendingStatusStrip from "./PendingStatusStrip";
import { ResultsTopNav } from "./ResultsTopNav";

interface Props {
  outfitId: string;
  previewDataUrl: string | null;
  isGuest: boolean;
  onReady: (
    row: Record<string, unknown>,
    items: Record<string, unknown>[],
  ) => void;
  onBack: () => void;
}



/**
 * Pending UI for shares received via the Android/iOS share intent. Renders the
 * shared image immediately from the base64 preview, then polls the outfits
 * row until analysis_status ∈ {items_ready, complete} and hands off to the
 * full Results screen via onReady(row, items). LB1/LB2/LB3 stream in via the
 * ResultsView realtime subscription, with LookbookLoadingSilhouette covering
 * the loading visual until each URL arrives.
 *
 * F.5-PERF-1 SMALL: handoff gate moved from `lookbook_image_url` (LB1 ready,
 * ~80s avg) to `analysis_status` (items_ready, ~18-31s avg), mirroring the
 * URL/Text flows. Net perceived-latency win ~50s on share flows.
 *
 * Polling (not realtime) chosen for parity with the URL/text watchers in
 * src/routes/index.tsx — keeps recovery semantics (visibility-resume,
 * App.resume broadcast, wall-clock cap) consistent across all pending flows.
 */
export default function SharePendingResults({
  outfitId,
  previewDataUrl,
  isGuest,
  onReady,
  onBack,
}: Props) {
  const issueNumber = useIssueNumber(outfitId, isGuest);
  const [failed, setFailed] = useState<string | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!outfitId) return;
    const startedAt = Date.now();
    // Items_ready typically lands in 18-31s; cap at 90s to surface stalls
    // pre-handoff. Post-handoff, ResultsView's realtime subscription owns
    // LB completion (no client-side timeout for LB gen at this layer).
    const MAX_DURATION_MS = 90_000;
    let cancelled = false;

    const fetchRow = async () => {
      if (isGuest) {
        const result = await (supabase as unknown as {
          rpc: (n: string, p: Record<string, unknown>) => Promise<{ data: unknown }>;
        }).rpc("get_guest_outfit", { p_id: outfitId });
        const rows = result.data as Record<string, unknown>[] | null;
        return Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
      }
      const result = await supabase
        .from("outfits")
        .select("*")
        .eq("id", outfitId)
        .maybeSingle();
      return (result.data as Record<string, unknown> | null) ?? null;
    };

    const fetchItems = async () => {
      if (isGuest) {
        const result = await (supabase as unknown as {
          rpc: (n: string, p: Record<string, unknown>) => Promise<{ data: unknown }>;
        }).rpc("get_guest_outfit_items", { p_outfit_id: outfitId });
        return (result.data as Record<string, unknown>[] | null) ?? [];
      }
      const result = await supabase
        .from("detected_items")
        .select("*")
        .eq("outfit_id", outfitId)
        .order("position", { ascending: true });
      return (result.data as Record<string, unknown>[] | null) ?? [];
    };

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        setFailed("This is taking longer than expected. Check Recents in a minute.");
        return;
      }
      const row = await fetchRow();
      if (cancelled || !row) return;
      const analysisStatus = (row.analysis_status as string | null) ?? null;
      const lbStatus = (row.lookbook_status as string | null) ?? null;
      const lbError = (row.lookbook_error as string | null) ?? null;

      // Pre-handoff failure: analyse-outfit-* edge fn flagged the row as
      // failed before items_ready. After handoff, LB failures are owned by
      // ResultsView's realtime subscription (silhouette stays pulsing —
      // MEDIUM scope adds an "unavailable" card).
      if (analysisStatus === "failed" || lbStatus === "failed") {
        setFailed(lbError ?? "Couldn't process this image.");
        return;
      }

      // F.5-PERF-1: hand off as soon as items + verdict data are ready.
      // Mirrors index.tsx L1717 (Text flow). LB1/LB2/LB3 cards render
      // LookbookLoadingSilhouette and stream URLs in via realtime.
      if (analysisStatus === "items_ready" || analysisStatus === "complete") {
        cancelled = true;
        const items = await fetchItems();
        onReadyRef.current(row, items);
        return;
      }
    };

    void poll();
    const intervalId = window.setInterval(() => { void poll(); }, 2000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void poll();
    };
    // F.5f — Capacitor App.resume broadcast. iOS WebView lock/unlock does
    // not always fire `visibilitychange`, so without this the poller can sit
    // idle on resume even when the server already finished. Idempotent: the
    // `cancelled` flag and items_ready handoff gate prevent double-transition.
    const onResume = () => { void poll(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("l2f:resume", onResume);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("l2f:resume", onResume);
    };
  }, [outfitId, isGuest]);

  const headline = "Analysing your outfit…";
  const subline = "Spotting each item and matching styles.";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF6EF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @keyframes l2f-share-pending-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes l2f-share-pending-spin { to { transform: rotate(360deg); } }
      `}</style>
      <ResultsTopNav
        issueNumber={issueNumber}
        isSaved={false}
        onBack={onBack}
        onBookmarkTap={onBack}
      />
      <PendingStatusStrip
        status={failed ? "failed_analysis" : "analysing"}
        notifyEnabled={NOTIFY_ON_READY}
      />
      <div style={{ padding: "16px 16px 96px" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "3 / 4",
            background: "#FFFCF8",
            border: "0.5px solid #E8E2D9",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          {previewDataUrl ? (
            <img
              src={previewDataUrl}
              alt="Your shared outfit"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "#FAECE7",
              }}
            />
          )}
          {/* Status overlay */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              padding: 20,
              gap: 8,
            }}
          >
            {failed ? (
              <>
                <div
                  style={{
                    fontSize: 18,
                    fontStyle: "italic",
                    fontFamily:
                      "var(--font-serif), 'Instrument Serif', Georgia, serif",
                    color: "#FFF",
                    lineHeight: 1.3,
                  }}
                >
                  Something went wrong
                </div>
                <div
                  className="l2f-type-caption"
                  style={{ color: "#FFF", opacity: 0.9, lineHeight: 1.4 }}
                >
                  {failed}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: 0.7,
                    animation: "l2f-share-pending-pulse 1.6s ease-in-out infinite",
                  }}
                >
                  <i
                    className="ti ti-loader-2"
                    style={{
                      fontSize: 20,
                      color: "#FFF",
                      animation: "l2f-share-pending-spin 1s linear infinite",
                    }}
                  />
                  <div
                    style={{
                      fontSize: 20,
                      fontStyle: "italic",
                      fontFamily:
                        "var(--font-serif), 'Instrument Serif', Georgia, serif",
                      color: "#FFF",
                      lineHeight: 1.2,
                    }}
                  >
                    {headline}
                  </div>
                </div>
                <div
                  className="l2f-type-caption"
                  style={{
                    color: "#FFF",
                    opacity: 0.9,
                    maxWidth: 280,
                    lineHeight: 1.45,
                  }}
                >
                  {subline}
                </div>
              </>
            )}
          </div>
        </div>

        {failed ? (
          <button
            type="button"
            onClick={onBack}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "14px 20px",
              borderRadius: 14,
              background: "#2C2820",
              color: "#FAF6EF",
              border: "none",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Back to home
          </button>
        ) : (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  height: 56,
                  borderRadius: 14,
                  background: "#FFFCF8",
                  border: "0.5px solid #E8E2D9",
                  animation: "l2f-share-pending-pulse 1.6s ease-in-out infinite",
                  animationDelay: `${i * 0.12}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
